import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';
import { FIELD_ERROR, FIELD_LABEL } from './field-styles';
import { uniqueId } from './ids';

export interface RadioOption {
  readonly value: string;
  readonly label: string;
  /** Una riga che spiega l'opzione, per esempio cosa consente un permesso (FR-017a). */
  readonly description?: string;
}

/**
 * Scelta singola con descrizione (tavola 3A, "Permessi"): `fieldset` con `legend` e radio veri,
 * quindi frecce e Tab funzionano come nel browser. La legenda può ospitare un aiuto proiettato.
 */
@Component({
  selector: 'bs-radio-group',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `
    <fieldset class="flex flex-col gap-2" [attr.data-testid]="testId()" [disabled]="disabled()">
      <legend [class]="labelClass + ' flex items-center gap-1'">
        {{ label() }}<ng-content select="[legend]" />
      </legend>
      @for (option of options(); track option.value) {
        <label
          class="flex min-h-11 cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 transition-colors"
          [class]="
            option.value === value()
              ? 'border-ink bg-bsite-100 dark:border-bsite-400 dark:bg-bsite-950'
              : 'border-stone-300 bg-white hover:bg-stone-50 dark:border-stone-700 dark:bg-stone-900 dark:hover:bg-stone-800'
          "
        >
          <input
            type="radio"
            class="mt-0.5 size-5 shrink-0 cursor-pointer accent-ink dark:accent-bsite-400"
            [name]="name"
            [value]="option.value"
            [checked]="option.value === value()"
            [attr.data-testid]="testId() ? testId() + '-' + option.value : null"
            (change)="value.set(option.value)"
          />
          <span class="flex flex-col gap-0.5">
            <span class="text-sm font-semibold text-ink dark:text-stone-50">{{
              option.label
            }}</span>
            @if (option.description; as description) {
              <span class="text-sm text-stone-600 dark:text-stone-300">{{ description }}</span>
            }
          </span>
        </label>
      }
      @if (error(); as message) {
        <p [class]="errorClass">{{ message }}</p>
      }
    </fieldset>
  `,
})
export class RadioGroupComponent {
  readonly label = input.required<string>();
  readonly options = input.required<readonly RadioOption[]>();
  readonly value = model<string | null>(null);
  readonly error = input<string | null>(null);
  readonly disabled = input(false);
  readonly testId = input<string | null>(null);

  protected readonly name = uniqueId('bs-radio-group');
  protected readonly labelClass = FIELD_LABEL;
  protected readonly errorClass = FIELD_ERROR;
}
